/**
 * ReportBugTab — team members (not admins) send a bug to the AI fix team
 * (devmode-v1.html §4). Page route + URL are taken automatically; the note is
 * typed; a picture is optional and goes through the existing upload endpoint.
 */
import { useRef, useState } from 'react';
import { uploadFile } from '@/utils/uploadFile';
import { KIAAN_UNREACHABLE, reportBug } from './kiaanApi';

export const BUG_SENT_TEXT = 'Got it. Sent to the fix team. I will tell you when it is fixed.';

export function ReportBugTab({ pathname }: { pathname: string }) {
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const pageUrl = typeof window !== 'undefined' ? window.location.href : pathname;

  const send = async () => {
    const trimmed = note.trim();
    if (!trimmed || state === 'sending') return;
    setState('sending');
    setError(null);
    try {
      let imageFileId: string | undefined;
      if (file) {
        const up = await uploadFile(file, { entity: 'kiaan_bug' });
        imageFileId = up.id;
      }
      await reportBug({ route: pathname, pageUrl, note: trimmed, imageFileId });
      setState('sent');
    } catch (err) {
      setState('error');
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 403 ? 'You cannot report a bug with this login. Ask your admin.' : KIAAN_UNREACHABLE);
    }
  };

  const reset = () => {
    setNote('');
    setFile(null);
    setState('idle');
    setError(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="kw-body">
      <div className="kw-msgs">
        <div className="kw-m kw-bot">What went wrong? I noted this page for you.</div>
        <div className="kw-card">
          <div className="kw-small">This page</div>
          <div className="kw-mono">{pathname}</div>
          <div className="kw-small" style={{ wordBreak: 'break-all' }}>{pageUrl}</div>
          {file && <div className="kw-small" style={{ marginTop: 4 }}>Picture: {file.name}</div>}
        </div>
        {state === 'sent' && (
          <>
            <div className="kw-m kw-me">{note.trim()}</div>
            <div className="kw-m kw-bot" role="status">{BUG_SENT_TEXT}</div>
            <button type="button" className="kw-btn kw-light" style={{ alignSelf: 'flex-start' }} onClick={reset}>Report another</button>
          </>
        )}
        {state === 'error' && error && <div className="kw-banner kw-danger" role="alert" style={{ margin: 0 }}>{error}</div>}
      </div>
      {state !== 'sent' && (
        <div className="kw-in" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Describe the problem…"
            aria-label="Describe the problem"
            rows={3}
            disabled={state === 'sending'}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              aria-label="Add a picture"
              style={{ display: 'none' }}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <button type="button" className="kw-btn kw-light" style={{ marginTop: 0 }} onClick={() => fileRef.current?.click()} disabled={state === 'sending'}>
              {file ? 'Change picture' : 'Add a picture'}
            </button>
            <span className="kw-sp" />
            <button type="button" className="kw-btn" style={{ marginTop: 0 }} onClick={() => void send()} disabled={!note.trim() || state === 'sending'}>
              {state === 'sending' ? 'Sending…' : 'Send to the fix team'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
